import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pageTabsSx, primaryButtonSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Tabs,
  Tab,
  Snackbar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import LocationFilesExplorer, { FilesExplorerLocation } from '../../components/LocationFilesExplorer';
import { fetchBusinessFeaturePermissions } from '../../utils/businessPermissions';
import { locationLabel } from '../../utils/terminology';
import {
  applyLocationFilters,
  DEFAULT_LOCATION_FILTERS,
  filtersFromLocationView,
  isLocationFiltersDefault,
  type BusinessLocationView,
  type LocationFilters,
} from '../../utils/locationFilters';

interface Location {
  locationId: string;
  locationName: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  assetCount?: number;
}

const LOCATION_TABS = ['list', 'files', 'import'] as const;

const tabSlugToIndex = (tabParam: string | null): number => {
  if (tabParam === 'files') return 1;
  if (tabParam === 'import') return 2;
  return 0;
};

const LocationManagement: React.FC<{ standaloneViewId?: string }> = ({ standaloneViewId }) => {
  const isStandaloneView = Boolean(standaloneViewId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() =>
    tabSlugToIndex(new URLSearchParams(window.location.search).get('tab')),
  );
  const [useSiteTerminology, setUseSiteTerminology] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    locationName: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    latitude: '',
    longitude: '',
    radius: '100',
  });
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationFilters, setLocationFilters] = useState<LocationFilters>(DEFAULT_LOCATION_FILTERS);
  const [standaloneView, setStandaloneView] = useState<BusinessLocationView | null>(null);
  const [standaloneViewLoading, setStandaloneViewLoading] = useState(isStandaloneView);
  const [standaloneViewError, setStandaloneViewError] = useState<string | null>(null);

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importValidating, setImportValidating] = useState(false);
  const [importExecuting, setImportExecuting] = useState(false);
  const [validatedData, setValidatedData] = useState<any>(null);
  const [importResults, setImportResults] = useState<any>(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<number, 'skip' | 'update'>>({});

  useEffect(() => {
    fetchLocations();
    fetchBusinessFeaturePermissions()
      .then((perms) => setUseSiteTerminology(perms.useSiteTerminology))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!standaloneViewId) return;
    let cancelled = false;
    (async () => {
      setStandaloneViewLoading(true);
      setStandaloneViewError(null);
      try {
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) throw new Error('Not authenticated');
        const { data } = await axios.get(`${API_BASE_URL}/businesses/${businessId}/location-views`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const views: BusinessLocationView[] = Array.isArray(data.location_views) ? data.location_views : [];
        const view = views.find(v => v.id === standaloneViewId);
        if (cancelled) return;
        if (!view) {
          setStandaloneView(null);
          setStandaloneViewError('This location view could not be found.');
          return;
        }
        setStandaloneView(view);
        const { filters, searchQuery } = filtersFromLocationView(view);
        setLocationFilters(filters);
        setLocationSearchQuery(searchQuery);
      } catch (e: unknown) {
        if (!cancelled) {
          setStandaloneViewError(e instanceof Error ? e.message : 'Failed to load location view');
        }
      } finally {
        if (!cancelled) setStandaloneViewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [standaloneViewId]);

  useEffect(() => {
    const idx = tabSlugToIndex(tabParam);
    setActiveTab((prev) => (idx !== prev ? idx : prev));
  }, [tabParam]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: LOCATION_TABS[newValue] }, { replace: true });
    }
  };

  const termLabel = locationLabel(useSiteTerminology);
  const termLabelPlural = locationLabel(useSiteTerminology, { plural: true });

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/locations/export/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'locations_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSnackbar({ open: true, message: 'Template downloaded successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to download template', severity: 'error' });
    }
  };

  const handleExportLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/locations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `locations_export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSnackbar({ open: true, message: `${termLabelPlural} exported successfully`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: `Failed to export ${termLabelPlural.toLowerCase()}`, severity: 'error' });
    }
  };

  const handleImportFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportValidating(true);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/locations/import/validate`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setValidatedData(response.data.data);
      setSnackbar({
        open: true,
        message: response.data.data.hasErrors ? 'Validation completed with errors' : 'File validated successfully',
        severity: response.data.data.hasErrors ? 'error' : 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to validate file',
        severity: 'error',
      });
    } finally {
      setImportValidating(false);
      event.target.value = '';
    }
  };

  const handleExecuteImport = async () => {
    if (!validatedData || validatedData.hasErrors) return;

    setImportExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/locations/import/execute`,
        { rows: validatedData.rows, decisions: duplicateDecisions },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );

      setImportResults(response.data.data);
      setSnackbar({
        open: true,
        message: `Import completed: ${response.data.data.successCount} successful, ${response.data.data.failureCount} failed`,
        severity: 'success',
      });

      fetchLocations();
      setImportFile(null);
      setValidatedData(null);
      setDuplicateDecisions({});
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to execute import',
        severity: 'error',
      });
    } finally {
      setImportExecuting(false);
    }
  };

  const handleDuplicateDecision = (rowNumber: number, decision: 'skip' | 'update') => {
    setDuplicateDecisions((prev) => ({ ...prev, [rowNumber]: decision }));
  };

  const handleClearImport = () => {
    setImportFile(null);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});
  };

  const filteredLocations = useMemo(
    () => applyLocationFilters(locations, locationFilters, locationSearchQuery),
    [locations, locationFilters, locationSearchQuery]
  );

  const filtersActive = !isLocationFiltersDefault(locationFilters, locationSearchQuery);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Debug the response
      console.log('API response:', response.data);
      
      // Handle different response formats
      let rawArray: any[] = [];
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        rawArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        rawArray = response.data;
      } else {
        console.error('Unexpected response format:', response.data);
        rawArray = [];
      }

      const locationData: Location[] = rawArray.map((item: any) => ({
        locationId: item.locationId || item.location_id || item.id,
        locationName: item.locationName || item.location_name || item.name,
        address: item.address || '',
        contactPerson: item.contactPerson || item.contact_person || '',
        contactPhone: item.contactPhone || item.contact_phone || '',
        latitude: typeof item.latitude === 'number' ? item.latitude : (item.latitude ? parseFloat(item.latitude) : undefined),
        longitude: typeof item.longitude === 'number' ? item.longitude : (item.longitude ? parseFloat(item.longitude) : undefined),
        radius: typeof item.radius === 'number' ? item.radius : (item.radius ? parseInt(item.radius, 10) : undefined),
        assetCount: Number(item.assetCount ?? item.asset_count ?? 0),
      }));

      setLocations(locationData);
      setError(null);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setSelectedLocation(null);
    setFormData({
      locationName: '',
      address: '',
      contactPerson: '',
      contactPhone: '',
      latitude: '',
      longitude: '',
      radius: '100',
    });
    setOpenDialog(true);
  };

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setFormData({
      locationName: location.locationName,
      address: location.address || '',
      contactPerson: location.contactPerson || '',
      contactPhone: location.contactPhone || '',
      latitude: (location.latitude ?? '').toString(),
      longitude: (location.longitude ?? '').toString(),
      radius: (location.radius ?? '100').toString(),
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLocation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      console.log('Token from localStorage:', token); // Debug log
      
      if (!token) {
        console.error('No authentication token found');
        setError('Please log in to continue');
        return;
      }

      const url = selectedLocation
        ? `${API_BASE_URL}/locations/${selectedLocation.locationId}`
        : `${API_BASE_URL}/locations`;
      const method = selectedLocation ? 'PUT' : 'POST';

      console.log('Making request with:', { // Debug log
        method,
        url,
        data: formData,
        headers: { Authorization: `Bearer ${token}` }
      });

      let payload: any;
      if (method === 'POST') {
        const businessId = localStorage.getItem('businessId');
        if (!businessId) {
          setError('Business context missing. Please select a business.');
          return;
        }

        const lat = formData.latitude ? parseFloat(formData.latitude) : NaN;
        const lng = formData.longitude ? parseFloat(formData.longitude) : NaN;
        if (!isFinite(lat) || !isFinite(lng)) {
          setError('Please provide valid latitude and longitude.');
          return;
        }

        payload = {
          location_name: formData.locationName,
          latitude: lat,
          longitude: lng,
          radius: formData.radius ? parseInt(formData.radius, 10) : 100,
          business_id: businessId,
          address: formData.address || '',
          description: '',
          contact_person: formData.contactPerson || '',
          contact_phone: formData.contactPhone || '',
        };
      } else {
        // Update uses camelCase as expected by backend update route
        payload = {
          locationName: formData.locationName,
          address: formData.address || '',
          contactPerson: formData.contactPerson || '',
          contactPhone: formData.contactPhone || '',
        };
      }

      const response = await axios({
        method,
        url,
        data: payload,
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Response:', response); // Debug log

      fetchLocations();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving location:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        setError(error.response.data.message || 'Failed to save location');
      } else {
        setError('Failed to save location');
      }
    }
  };

  const handleDelete = async (locationId: string) => {
    const location = locations.find((l) => l.locationId === locationId);
    const assetCount = Number(location?.assetCount || 0);
    const term = locationLabel(useSiteTerminology, { lowercase: true });

    if (assetCount > 0) {
      setError(
        `Cannot delete this ${term} because it has ${assetCount} asset${assetCount === 1 ? '' : 's'} assigned. Move or delete those assets first.`,
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this ${term}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      await axios.delete(`${API_BASE_URL}/locations/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchLocations();
    } catch (error: any) {
      console.error('Error deleting location:', error);
      setError(error.response?.data?.message || `Failed to delete ${term}`);
    }
  };

  // Files tab always lists every location — filters on the list tab do not apply here
  const explorerLocations: FilesExplorerLocation[] = useMemo(
    () => locations.map((l) => ({
      locationId: l.locationId,
      locationName: l.locationName,
      address: l.address,
    })),
    [locations],
  );

  const pageHeaderAction =
    !isStandaloneView && activeTab === 0 ? (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenDialog}
        sx={primaryButtonSx}
      >
        Add {termLabel}
      </Button>
    ) : !isStandaloneView && activeTab === 2 ? (
      <Button
        variant="outlined"
        startIcon={<FileUploadIcon />}
        component="label"
        htmlFor="location-import-file-upload"
      >
        Upload File
      </Button>
    ) : null;

  if (isStandaloneView && standaloneViewLoading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (isStandaloneView && standaloneViewError) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}>{standaloneViewError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title={isStandaloneView ? (standaloneView?.label ?? `${termLabel} view`) : locationLabel(useSiteTerminology, { plural: true })}
        subtitle={isStandaloneView ? 'Custom view' : 'Operations'}
        action={pageHeaderAction}
      />

      {!isStandaloneView && (
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={pageTabsSx}
        variant="fullWidth"
      >
        <Tab label={locationLabel(useSiteTerminology, { plural: true })} />
        <Tab label={`${locationLabel(useSiteTerminology)} Files`} />
        <Tab label="Import" icon={<FileUploadIcon fontSize="small" />} iconPosition="start" />
      </Tabs>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
      {activeTab === 1 && !isStandaloneView && (
        <Box sx={{ mt: 2 }}>
        <LocationFilesExplorer
          locations={explorerLocations}
          loadingLocations={loading}
          onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
          useSiteTerminology={useSiteTerminology}
        />
        </Box>
      )}

      {activeTab === 2 && !isStandaloneView && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleDownloadTemplate}>
              Download Template (.xlsx)
            </Button>
            <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleExportLocations}>
              Export Existing {termLabelPlural}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Required columns: <strong>{termLabel} Name</strong>, <strong>Latitude</strong>, and <strong>Longitude</strong>.
            Optional: Radius (meters), Address, Contact Person, Contact Phone.
            Supports <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.xlsm</strong>, and <strong>.csv</strong> files.
            Duplicate names can be skipped or updated.
          </Alert>

          {!importResults && (
            <Paper sx={{ mb: 2.5, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <input
                accept=".xlsx,.xls,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                style={{ display: 'none' }}
                id="location-import-file-upload"
                type="file"
                onChange={handleImportFileSelect}
              />
              <label htmlFor="location-import-file-upload">
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {importFile ? importFile.name : `Click to upload Excel or CSV file`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports .xlsx, .xls, .xlsm, and .csv files
                  </Typography>
                </Box>
              </label>
            </Paper>
          )}

          {importValidating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {validatedData && !importResults && (
            <Paper sx={{ p: 3, mb: 2, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Validation Results</Typography>
                <Button onClick={handleClearImport}>Clear</Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${validatedData.totalRows}`} />
                <Chip label={`Valid: ${validatedData.validRows}`} color="success" />
                {validatedData.rowsWithErrors > 0 && (
                  <Chip label={`Errors: ${validatedData.rowsWithErrors}`} color="error" />
                )}
                {validatedData.duplicatesFound > 0 && (
                  <Chip label={`Duplicates: ${validatedData.duplicatesFound}`} color="warning" />
                )}
              </Box>

              {validatedData.hasErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the errors before importing
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>{termLabel} Name</TableCell>
                      <TableCell>Latitude</TableCell>
                      <TableCell>Longitude</TableCell>
                      <TableCell>Radius</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Contact Person</TableCell>
                      <TableCell>Contact Phone</TableCell>
                      <TableCell>Status</TableCell>
                      {validatedData.duplicatesFound > 0 && <TableCell>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validatedData.rows.map((row: any) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.data.location_name}</TableCell>
                        <TableCell>{row.data.latitude ?? '—'}</TableCell>
                        <TableCell>{row.data.longitude ?? '—'}</TableCell>
                        <TableCell>{row.data.radius ?? '—'}</TableCell>
                        <TableCell>{row.data.address || '—'}</TableCell>
                        <TableCell>{row.data.contact_person || '—'}</TableCell>
                        <TableCell>{row.data.contact_phone || '—'}</TableCell>
                        <TableCell>
                          {row.errors.length > 0 ? (
                            <Tooltip
                              title={
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {row.errors.map((err: string, idx: number) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </Box>
                              }
                            >
                              <Chip label={`Error (${row.errors.length})`} color="error" size="small" />
                            </Tooltip>
                          ) : row.isDuplicate ? (
                            <Chip label="Duplicate" color="warning" size="small" />
                          ) : (
                            <Chip label="Valid" color="success" size="small" />
                          )}
                        </TableCell>
                        {validatedData.duplicatesFound > 0 && (
                          <TableCell>
                            {row.isDuplicate && (
                              <ButtonGroup size="small">
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'skip' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'skip')}
                                >
                                  Skip
                                </Button>
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'update' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'update')}
                                >
                                  Update
                                </Button>
                              </ButtonGroup>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!validatedData.hasErrors && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleExecuteImport} disabled={importExecuting} sx={primaryButtonSx}>
                    {importExecuting ? <CircularProgress size={24} /> : 'Execute Import'}
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {importResults && (
            <Paper sx={{ p: 3, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom>
                Import Complete
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${importResults.totalRows}`} />
                <Chip label={`Successful: ${importResults.successCount}`} color="success" />
                <Chip label={`Failed: ${importResults.failureCount}`} color="error" />
                <Chip label={`Skipped: ${importResults.skippedCount}`} color="default" />
              </Box>
              <Button variant="contained" onClick={handleClearImport} sx={primaryButtonSx}>
                Import More {termLabelPlural}
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {(isStandaloneView || activeTab === 0) && (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${locationLabel(useSiteTerminology, { plural: true, lowercase: true })}…`}
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: locationSearchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setLocationSearchQuery('')} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Coordinates</InputLabel>
                  <Select
                    label="Coordinates"
                    value={locationFilters.hasCoordinates}
                    onChange={(e) => setLocationFilters({ ...locationFilters, hasCoordinates: e.target.value })}
                  >
                    <MenuItem value="all">Any</MenuItem>
                    <MenuItem value="true">Has coordinates</MenuItem>
                    <MenuItem value="false">Missing coordinates</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Contact</InputLabel>
                  <Select
                    label="Contact"
                    value={locationFilters.hasContact}
                    onChange={(e) => setLocationFilters({ ...locationFilters, hasContact: e.target.value })}
                  >
                    <MenuItem value="all">Any</MenuItem>
                    <MenuItem value="true">Has contact</MenuItem>
                    <MenuItem value="false">No contact</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Address</InputLabel>
                  <Select
                    label="Address"
                    value={locationFilters.hasAddress}
                    onChange={(e) => setLocationFilters({ ...locationFilters, hasAddress: e.target.value })}
                  >
                    <MenuItem value="all">Any</MenuItem>
                    <MenuItem value="true">Has address</MenuItem>
                    <MenuItem value="false">No address</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {filtersActive && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {locationSearchQuery && (
                  <Chip
                    label={`Search: "${locationSearchQuery}"`}
                    size="small"
                    onDelete={() => setLocationSearchQuery('')}
                  />
                )}
                {locationFilters.hasCoordinates !== 'all' && (
                  <Chip
                    label={`Coordinates: ${locationFilters.hasCoordinates === 'true' ? 'yes' : 'no'}`}
                    size="small"
                    onDelete={() => setLocationFilters({ ...locationFilters, hasCoordinates: 'all' })}
                  />
                )}
                {locationFilters.hasContact !== 'all' && (
                  <Chip
                    label={`Contact: ${locationFilters.hasContact === 'true' ? 'yes' : 'no'}`}
                    size="small"
                    onDelete={() => setLocationFilters({ ...locationFilters, hasContact: 'all' })}
                  />
                )}
                {locationFilters.hasAddress !== 'all' && (
                  <Chip
                    label={`Address: ${locationFilters.hasAddress === 'true' ? 'yes' : 'no'}`}
                    size="small"
                    onDelete={() => setLocationFilters({ ...locationFilters, hasAddress: 'all' })}
                  />
                )}
                <Chip
                  label="Clear all"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setLocationSearchQuery('');
                    setLocationFilters(DEFAULT_LOCATION_FILTERS);
                  }}
                />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Showing {filteredLocations.length} of {locations.length}
            </Typography>
          </Paper>

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredLocations.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No {locationLabel(useSiteTerminology, { plural: true, lowercase: true })} match your filters.</Typography>
            </Paper>
          ) : filteredLocations.map((location) => (
            <Paper key={location.locationId} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {location.locationName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {location.address}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditLocation(location)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(location.locationId)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Contact Person:</strong> {location.contactPerson || 'Not specified'}
                </Typography>
                <Typography variant="body2">
                  <strong>Contact Phone:</strong> {location.contactPhone || 'Not specified'}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{termLabel} Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Contact Phone</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No {locationLabel(useSiteTerminology, { plural: true, lowercase: true })} match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredLocations.map((location) => (
                <TableRow key={location.locationId}>
                  <TableCell>{location.locationName}</TableCell>
                  <TableCell>{location.address}</TableCell>
                  <TableCell>{location.contactPerson}</TableCell>
                  <TableCell>{location.contactPhone}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditLocation(location)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(location.locationId)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
        </Box>
      )}
        </>
      )}

      <Dialog
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedLocation ? `Edit ${termLabel}` : `Add ${termLabel}`}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`${termLabel} Name`}
                  value={formData.locationName}
                  onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  required={!selectedLocation}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  required={!selectedLocation}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Radius (meters)"
                  type="number"
                  value={formData.radius}
                  onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedLocation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default LocationManagement; 